import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { resolveMediaUrl } from '../../lib/api';
import { morphFrom, prefersReducedMotion } from '../../lib/motion';
import PizzaArt from './PizzaArt';
import { IngredientSprite, spriteForName, dropPositions } from './Ingredients';
import AnimatedPrice from './AnimatedPrice';

const SIZE_SCALE = [0.85, 0.93, 1];

/**
 * Hoja de producto: bottom sheet en móvil (arrastrar hacia abajo para cerrar), modal de 2 columnas en escritorio.
 * #6 ingredientes que caen · #7 tamaño que escala la pizza con indicador deslizante · #8 precio que cuenta · #10 morph desde la tarjeta.
 * La lógica de precio/carrito vive en MenuOrderPage; aquí solo se presenta.
 */
export default function ProductSheet({
  item,
  originRect,
  sizeKey,
  setSizeKey,
  halfId,
  setHalfId,
  extraIds,
  toggleExtra,
  allPizzas,
  toppingExtras,
  unitPrice,
  onConfirm,
  onClose,
  mediaRef,
}) {
  const sheetRef = useRef(null);
  const closeBtnRef = useRef(null);
  const localMediaRef = useRef(null);
  const [dragY, setDragY] = useState(0);
  const dragStart = useRef(null);
  const titleId = `sheet-title-${item.id}`;

  const sizes = item.sizes || [];
  const sizeIndex = Math.max(0, sizes.findIndex((s) => s.key === sizeKey));
  const scale = SIZE_SCALE[Math.min(sizeIndex, SIZE_SCALE.length - 1)] ?? 1;
  const img = item.image_url ? resolveMediaUrl(item.image_url) : '';
  const half = halfId ? allPizzas.find((p) => p.id === halfId) : null;
  const halfImg = half?.image_url ? resolveMediaUrl(half.image_url) : '';
  const selectedExtras = toppingExtras.filter((t) => extraIds.includes(t.id));

  // Morph desde la tarjeta + foco inicial
  useLayoutEffect(() => {
    morphFrom(localMediaRef.current, originRect);
  }, [originRect]);

  useEffect(() => {
    const prevFocus = document.activeElement;
    closeBtnRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'Tab' && sheetRef.current) {
        const f = sheetRef.current.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!f.length) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      if (prevFocus && prevFocus.focus) prevFocus.focus();
    };
  }, [onClose]);

  // Arrastrar para cerrar (solo desde el asa / cabecera en móvil)
  const onPointerDown = (e) => {
    if (window.matchMedia?.('(min-width: 768px)').matches) return;
    dragStart.current = e.clientY;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (dragStart.current == null) return;
    setDragY(Math.max(0, e.clientY - dragStart.current));
  };
  const onPointerUp = () => {
    if (dragStart.current == null) return;
    dragStart.current = null;
    if (dragY > 110) onClose();
    else setDragY(0);
  };

  const setMedia = (el) => {
    localMediaRef.current = el;
    if (mediaRef) mediaRef.current = el;
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center" role="presentation">
      <div className="t-sheet-backdrop absolute inset-0 bg-[#2B1A12]/55" onClick={onClose} aria-hidden="true" />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="t-sheet relative w-full md:max-w-4xl bg-[#FFFDF8] rounded-t-[28px] md:rounded-[28px] shadow-2xl max-h-[92dvh] flex flex-col md:grid md:grid-cols-2 overflow-hidden"
        style={{
          transform: dragY ? `translateY(${dragY}px)` : undefined,
          transition: dragStart.current == null && !prefersReducedMotion() ? 'transform .25s ease' : 'none',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {/* Medio: pizza que escala y recibe ingredientes */}
        <div
          className="relative bg-gradient-to-br from-[#FFE7D6] via-[#FFF6EA] to-[#F9D9A8] md:h-full touch-none md:touch-auto"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <div className="md:hidden absolute top-2 left-1/2 -translate-x-1/2 w-12 h-1.5 rounded-full bg-[#2B1A12]/30" aria-hidden="true" />
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 z-10 w-11 h-11 rounded-full bg-[#FFFDF8] border border-[#F1DFC8] flex items-center justify-center shadow focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-[#F2B33D]"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="h-[34dvh] md:h-full min-h-[220px] flex items-center justify-center p-6">
            <div
              ref={setMedia}
              className="relative aspect-square h-full max-h-[380px] transition-transform duration-300"
              style={{ transform: `scale(${scale})`, transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }}
            >
              {img ? (
                <img src={img} alt={item.name} className="w-full h-full object-cover rounded-full shadow-xl" />
              ) : (
                <PizzaArt className="w-full h-full drop-shadow-[0_20px_25px_rgba(43,26,18,.3)]" />
              )}
              {half ? (
                <div className="absolute inset-0" style={{ clipPath: 'inset(0 0 0 50%)' }} aria-hidden="true">
                  {halfImg ? (
                    <img src={halfImg} alt="" className="w-full h-full object-cover rounded-full" />
                  ) : (
                    <PizzaArt className="w-full h-full" toppings={false} style={{ filter: 'hue-rotate(-12deg) saturate(1.2)' }} />
                  )}
                  <div className="absolute inset-y-[4%] left-1/2 w-[3px] -ml-[1.5px] bg-[#FFFDF8]/90" />
                </div>
              ) : null}
              {selectedExtras.map((t) =>
                dropPositions(t.id, 4).map((pos, i) => (
                  <span
                    key={`${t.id}-${i}`}
                    className="t-drop absolute"
                    style={{ left: `${pos.left}%`, top: `${pos.top}%`, '--rot': `${pos.rot}deg`, '--delay': `${pos.delay}s` }}
                    aria-hidden="true"
                  >
                    <IngredientSprite type={spriteForName(t.name)} size={30} className="drop-shadow" />
                  </span>
                )),
              )}
            </div>
          </div>
          {half ? (
            <span className="absolute bottom-3 left-1/2 -translate-x-1/2 max-w-[90%] truncate font-hand text-xl text-[#2B1A12] bg-[#FFFDF8]/90 px-3 rounded-full">
              ½ {item.name} + ½ {half.name}
            </span>
          ) : null}
        </div>

        {/* Opciones */}
        <div className="flex flex-col min-h-0 flex-1">
          <div className="overflow-y-auto px-5 md:px-7 pt-5 pb-4 space-y-6 flex-1">
            <div>
              <h2 id={titleId} className="font-heading text-3xl font-extrabold text-[#2B1A12]">
                {item.name}
              </h2>
              {item.description ? <p className="text-[#6B5444] mt-1">{item.description}</p> : null}
            </div>

            {sizes.length ? (
              <fieldset>
                <legend className="font-bold mb-2">Tamaño</legend>
                <div className="relative grid rounded-2xl bg-[#FFF6EA] border border-[#F1DFC8] p-1" style={{ gridTemplateColumns: `repeat(${sizes.length}, 1fr)` }}>
                  <span
                    className="t-seg-indicator absolute top-1 bottom-1 left-1 rounded-xl bg-[#C2410C] shadow"
                    style={{ width: `calc((100% - 0.5rem) / ${sizes.length})`, transform: `translateX(${sizeIndex * 100}%)` }}
                    aria-hidden="true"
                  />
                  {sizes.map((s) => {
                    const active = s.key === sizeKey;
                    return (
                      <label
                        key={s.key}
                        className={`relative z-[1] flex flex-col items-center justify-center min-h-[56px] rounded-xl cursor-pointer select-none transition-colors ${
                          active ? 'text-white' : 'text-[#2B1A12]'
                        } has-[:focus-visible]:outline has-[:focus-visible]:outline-[3px] has-[:focus-visible]:outline-[#F2B33D]`}
                      >
                        <input
                          type="radio"
                          name={`size-${item.id}`}
                          value={s.key}
                          checked={active}
                          onChange={() => setSizeKey(s.key)}
                          className="sr-only"
                        />
                        <span className="text-sm font-semibold">{s.label}</span>
                        <span className="font-bold tabular">${Number(s.price).toFixed(0)}</span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            ) : null}

            {allPizzas.length > 1 ? (
              <div>
                <label htmlFor={`half-${item.id}`} className="font-bold block mb-2">
                  Mitad y mitad <span className="font-semibold text-[#6B5444]">(+$40)</span>
                </label>
                <select
                  id={`half-${item.id}`}
                  className="w-full h-12 rounded-xl border border-[#F1DFC8] bg-[#FFF6EA] px-3 text-base focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-[#F2B33D]"
                  value={halfId}
                  onChange={(e) => setHalfId(e.target.value)}
                >
                  <option value="">Sin mitad</option>
                  {allPizzas
                    .filter((p) => p.id !== item.id)
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                </select>
              </div>
            ) : null}

            {toppingExtras.length ? (
              <fieldset>
                <legend className="font-bold mb-2">Ingredientes extra</legend>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {toppingExtras.map((t) => {
                    const checked = extraIds.includes(t.id);
                    const price = t.extra_prices_by_size?.[sizeKey] ?? 30;
                    return (
                      <label
                        key={t.id}
                        className={`flex items-center gap-2 rounded-2xl border-2 px-2.5 py-2 min-h-[48px] cursor-pointer transition-all ${
                          checked
                            ? 'border-[#C2410C] bg-[#FFE7D6] shadow-[2px_2px_0_#C2410C]'
                            : 'border-[#F1DFC8] bg-[#FFFDF8] hover:border-[#F2B33D]'
                        } has-[:focus-visible]:outline has-[:focus-visible]:outline-[3px] has-[:focus-visible]:outline-[#F2B33D]`}
                      >
                        <input type="checkbox" className="sr-only" checked={checked} onChange={() => toggleExtra(t.id)} />
                        <IngredientSprite type={spriteForName(t.name)} size={26} className="shrink-0" />
                        <span className="flex-1 text-sm font-semibold leading-tight">{t.name}</span>
                        <span className="text-xs font-bold text-[#6B5444] tabular">+${Number(price).toFixed(0)}</span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            ) : null}
          </div>

          <div className="border-t border-[#F1DFC8] px-5 md:px-7 py-4 bg-[#FFFDF8]">
            <button type="button" className="t-btn t-btn-primary w-full text-lg" onClick={onConfirm}>
              Agregar · <AnimatedPrice value={unitPrice} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
